import { serializeQueryParams } from '../../../helpers/url';
import * as keyTransformers from '../../../helpers/keysTransformers';
import { createHttp, ResponseOptions, RequestOptionsRaw } from '../../http';

export const core = createHttp<
  {
    /**
     * If `true` `accessToken` variable will be added as value of `X-Access-Token` header
     */
    accessToken?: boolean;
  },
  {
    /**
     * Base url (domain) for all requests
     */
    baseUrl: string;

    /**
     * Token that can be send as value of `X-Access-Token` header
     */
    accessToken: string | undefined;

    /**
     * Language that can be send as value of `Content-Language` header
     */
    lang: 'en' | 'ru';
  },
  {
    /**
     * Object will be converted to string and send as body
     */
    json?: Record<string, any>;

    /**
     * Object will be converted to query string
     *
     * @example { k1: 'v1', k2: 'v2' } => ?k1=v1&k2=v2
     */
    queryParams?: Record<string, any>;
  }
>();

core.setVariable('lang', 'ru');

core.addRequestInterceptor(
  ({ customOptions, url, params: { headers, ...params } }) => {
    headers = {
      ...headers,
      'Content-Language': core.getVariable('lang'),
    } as Record<string, string>;

    if (customOptions.accessToken) {
      const accessToken = core.getVariable('accessToken');
      if (accessToken) headers['X-Access-Token'] = accessToken;
    }

    if (params.json) {
      headers['Content-Type'] = 'application/json';
      const json = keyTransformers.decamelize(params.json);
      params.body = JSON.stringify(json);
    }

    url = `${core.getVariable('baseUrl')}${url}`;
    if (params.queryParams) {
      url = `${url}${serializeQueryParams(params.queryParams, {
        addQueryPrefix: true,
      })}`;
    }

    return {
      customOptions,
      url,
      params: {
        ...params,
        headers,
      },
    };
  }
);

core.addResponseInterceptor(({ response, options }) => {
  let { result } = response;
  if (result) {
    try {
      result = JSON.parse(result);
      result = keyTransformers.camelize(result);
    } catch {
      // no actions
    }
  }

  response = { ...response, result };

  if (response.error?.kind === 'response') {
    const { error, ...responseWithoutError } = response;
    error.message = getMessage(response, options);
    error.response = responseWithoutError;
  }

  return response;
});

const getMessage = (response: ResponseOptions, options: RequestOptionsRaw) => {
  const messages = response.result?.messages || [];
  const url = new URL(options.url);
  return JSON.stringify({
    status: response.status || 'unknown',
    path: url.pathname + url.search,
    body: messages.length > 0 ? messages[0] : response.result,
  });
};
